import { autoUpdate, arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { Component, ComponentInterface, Element, Host, JSX, Listen, Prop, Watch, h } from '@stencil/core';
import { TooltipPosition } from '../../../components/overlays/tooltip/tooltip-position';
import { transformTagNames } from '../../../global/transform-tag-name';
import { WorkbenchRuntimeError } from '../../../shared/errors';
import { Keys } from '../../../shared/keys';
import { getRandomId } from '../../../utils/get-random-id';
import { isTouchDevice } from '../../../utils/is-touch-device';

/**
 * @slot - Element that triggers the tooltip like a button or anchor
 * @slot content - Content to be displayed in the tooltip overlay
 */
@Component({
  tag: 'wb-tooltip',
  styleUrl: './tooltip.scss',
  shadow: true,
})
export class TooltipComponent implements ComponentInterface {
  private uniqueId = `wb-tooltip-overlay-${getRandomId()}}`;

  @Element()
  public el: HTMLElement;

  /** optional tooltip variant (inline display property to use inside other webcomponents, if styling is broken) */
  @Prop({ reflect: true })
  public variant?: 'inline';

  /** If present, shows overlay */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** position of the tooltip */
  @Prop({ reflect: true })
  public position: TooltipPosition = 'top';

  @Watch('position')
  public onConfigurationChange(): void {
    this.updatePosition();
  }

  @Watch('open')
  public onOpenChange(): void {
    if (!this.isTouchDevice) {
      return;
    }

    this.open //
      ? this.addCloseOnClickOutsideListener()
      : this.removeCloseOnClickOutsideListener();
  }

  @Listen('keydown', { target: 'window' })
  public onKeyDown(e: KeyboardEvent): void {
    if (this.open && e.key === Keys.ESC) {
      this.closeOverlay();
    }
  }

  private TagNames = transformTagNames(this, 'wb-tooltip', ['wb-text']);

  private overlayEl: HTMLElement;
  private triggerEl: HTMLElement;
  private arrowEl: HTMLElement;
  private isTouchDevice: boolean;

  private cleanup: () => void;

  public connectedCallback(): void {
    this.isTouchDevice = isTouchDevice();
  }

  public componentDidLoad(): void {
    if (this.open && this.isTouchDevice) {
      this.addCloseOnClickOutsideListener();
    }
  }

  private updatePosition = async (): Promise<void> => {
    let preferedPlacement;

    switch (this.position) {
      case 'top':
        preferedPlacement = 'top';
        break;
      case 'bottom':
        preferedPlacement = 'bottom';
        break;
      case 'start':
        preferedPlacement = 'left';
        break;
      case 'end':
        preferedPlacement = 'right';
        break;
    }

    const { x, y, placement, middlewareData } = await computePosition(this.triggerEl, this.overlayEl, {
      placement: preferedPlacement,
      strategy: 'absolute',
      middleware: [
        offset(8), //
        flip({
          padding: 16,
          fallbackAxisSideDirection: 'start',
        }),
        shift({ padding: 16 }),
        arrow({ element: this.arrowEl, padding: 8 }),
      ],
    });

    Object.assign(this.overlayEl.style, {
      left: `${x}px`,
      top: `${y}px`,
    });

    const { x: arrowX, y: arrowY } = middlewareData.arrow;

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]];

    Object.assign(this.arrowEl.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-4px',
    });
  };

  private openOverlay = async (): Promise<void> => {
    if (document.getElementById(this.uniqueId)) {
      return; // make sure overlay is only attached once
    }

    this.open = true;

    this.createOverlayEl();
    document.body.appendChild(this.overlayEl);
    await this.updatePosition();
  };

  private closeOverlay = async (): Promise<void> => {
    this.open = false;

    if (document.body.contains(this.overlayEl)) {
      this.overlayEl?.remove();
      this.arrowEl?.remove();
      this.cleanup();
    }
  };

  private addCloseOnClickOutsideListener = (): void => {
    window.addEventListener('touchstart', this.closeIfClickOutside);
  };

  private removeCloseOnClickOutsideListener = (): void => {
    window.removeEventListener('touchstart', this.closeIfClickOutside);
  };

  private closeIfClickOutside = (evt: Event): void => {
    const path = evt.composedPath();
    const isClickInside = path.includes(this.el);

    if (!isClickInside) {
      this.closeOverlay();
    }
  };

  private updateTriggerEl = (evt: Event): void => {
    const slottedElements = (evt.target as HTMLSlotElement).assignedElements();

    if (slottedElements.length !== 1) {
      throw new WorkbenchRuntimeError('exactly one trigger element must be slotted', 'wb-tooltip');
    }

    this.triggerEl = slottedElements[0] as HTMLElement;

    this.triggerEl.addEventListener('mouseenter', this.openOverlay);
    this.triggerEl.addEventListener('mouseleave', this.closeOverlay);
    this.triggerEl.addEventListener('focus', this.openOverlay);
    this.triggerEl.addEventListener('blur', this.closeOverlay);
  };

  private createOverlayEl() {
    const tooltipEl = document.createElement('div');
    const arrowEl = document.createElement('div');

    tooltipEl.id = this.uniqueId;

    // eslint-disable-next-line workbench/tag-name-transform
    tooltipEl.classList.add('wb-tooltip-overlay');
    Object.assign(tooltipEl.style, {
      borderRadius: 'var(--wb-radius-s)',
      backgroundColor: 'var(--wb-grey-20)',
      color: 'var(--wb-white)',
      padding: '16px',
      maxWidth: '320px',
      position: 'absolute',
      zIndex: '1500', // same z-index as modals to make sure tooltips within modals are visible
      boxSizing: 'border-box',
      pointerEvents: 'none',
      width: 'max-content',
      top: '0', // add top and left value initially to prevent scroll bar flickering (https://git.i.mercedes-benz.com/dh-io-workbench/core/issues/3548)
      left: '0',
    });

    // eslint-disable-next-line workbench/tag-name-transform
    arrowEl.classList.add('wb-tooltip-overlay-arrow');
    Object.assign(arrowEl.style, {
      position: 'absolute',
      background: 'var(--wb-grey-20)',
      width: '8px',
      height: '8px',
      transform: 'rotate(45deg)',
    });

    const overlayTextEl = document.createElement(this.TagNames.WbText) as HTMLWbTextElement;
    overlayTextEl.size = 'm';
    overlayTextEl.static = true;

    const contentSlotClone = this.el.querySelector('[slot="content"]').cloneNode(true) as HTMLElement;
    contentSlotClone.removeAttribute('slot');
    overlayTextEl.appendChild(contentSlotClone);

    tooltipEl.appendChild(overlayTextEl);
    tooltipEl.append(arrowEl);

    this.overlayEl = tooltipEl;
    this.arrowEl = arrowEl;

    this.cleanup = autoUpdate(this.triggerEl, this.overlayEl, this.updatePosition);
  }

  public render(): JSX.Element {
    return (
      <Host class="wb-tooltip" aria-labelledby={this.uniqueId}>
        <slot onSlotchange={this.updateTriggerEl} />
      </Host>
    );
  }
}
